import { z } from 'zod'

import i18n from 'modules/core/lib/i18n'

import { FormContext } from 'modules/workflows/components/Sidepanel/schemas/nodeSidepanelSchema'
import { createTaskFormSchema } from 'modules/workflows/components/Sidepanel/schemas/taskFormSchema'

export const createRequestDocumentHandlerSchema = (formContext: FormContext) =>
  createTaskFormSchema(formContext).extend({
    nodeType: z.literal('task'),
    nodeData: z.object({
      handlerType: z.literal('request_document'),
      payload: z
        .object({
          title: z.string().min(1, {
            message: i18n.t('procurement.workflows.basic_task_handler.validation.title_required'),
          }),
          description: z.string().min(1, {
            message: i18n.t(
              'procurement.workflows.basic_task_handler.validation.description_required'
            ),
          }),
          // assign_to_requester is derived from assignee_ids in the UI
          // but stored separately for backend compatibility
          assign_to_requester: z.boolean().optional(),
          // assignee_ids can include 'purchase_requester' string in the UI
          // but will be filtered out before sending to backend
          assignee_ids: z.array(z.string()).optional(),
          due_on_value: z
            .number({
              message: i18n.t(
                'procurement.workflows.basic_task_handler.validation.due_date_min_value'
              ),
            })
            .min(1, {
              message: i18n.t(
                'procurement.workflows.basic_task_handler.validation.due_date_min_value'
              ),
            }),
          due_on_unit: z.enum(['hours', 'days', 'weeks', 'months']),
        })
        .refine(
          (data) => {
            // At least one assignee must be selected:
            // - Either assign_to_requester is true, OR
            // - assignee_ids has at least one element (excluding 'purchase_requester' if it's the only one)
            const hasRequester = data.assign_to_requester === true
            const hasOtherAssignees =
              Array.isArray(data.assignee_ids) &&
              data.assignee_ids.filter((id) => id !== 'purchase_requester').length > 0

            return hasRequester || hasOtherAssignees
          },
          {
            message: i18n.t(
              'procurement.workflows.basic_task_handler.validation.assignee_required'
            ),
            path: ['assignee_ids'], // Show error on the assignee field
          }
        ),
    }),
  })

export type RequestDocumentHandlerSchema = z.infer<
  ReturnType<typeof createRequestDocumentHandlerSchema>
>
